import { clsxm } from "@/utils/clsxm";
import { useQuizContext } from "@/pages/quiz/context";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { SlidesProgress } from "@/pages/quiz/15000/components/ProgressSection/components/SlidesProgress";
import { useMemo } from "react";

const ProgressSection = () => {
  const { questions, selectedQuestionIdx, selectedQuestion, onBack } =
    useQuizContext();

  const sections = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const q of questions ?? []) {
      const c = q?.attributes?.progressCategory;
      if (!c || seen.has(c)) continue;
      seen.add(c);
      list.push(c);
    }
    return list;
  }, [questions]);

  const findPrevCategory = (idx: number): string | null => {
    if (!questions?.length) return null;
    for (let i = Math.min(idx, questions.length - 1); i >= 0; i--) {
      const c = questions[i]?.attributes?.progressCategory;
      if (c) return c;
    }
    return null;
  };

  const progressCategoryConfig = useMemo(() => {
    if (!questions?.length || selectedQuestionIdx == null) return null;

    const ownCategory =
      questions[selectedQuestionIdx]?.attributes?.progressCategory ?? null;
    const effectiveCategory =
      ownCategory || findPrevCategory(selectedQuestionIdx);
    if (!effectiveCategory) return null;

    const idxInSections = sections.indexOf(effectiveCategory);
    if (idxInSections === -1) return null;

    return {
      category: effectiveCategory,
      displayCategory: ownCategory ?? "",
      current: idxInSections + 1,
      total: sections.length,
      hasOwnCategory: !!ownCategory,
    };
  }, [questions, sections, selectedQuestionIdx]);

  const isHideBackButton = selectedQuestionIdx === 0;

  return (
    <div className="flex flex-col gap-4 px-4 w-full md:px-0">
      <div className="flex justify-between items-center gap-2 text-sm text-new-design-gray-500/50">
        <button
          className={clsxm(
            "w-[60px] flex-shrink-0 flex items-center gap-1 rounded-xl px-1 py-0.5 outline-none leading-none",
            "hover:cursor-pointer hover:bg-new-design-gray-500/5 hover:text-new-design-gray-500/70",
            isHideBackButton && "opacity-0 pointer-events-none"
          )}
          onClick={onBack}
        >
          <IoArrowBackCircleOutline size={14} />
          <span className="pt-0.5">Back</span>
        </button>
        <div className="flex-1 text-center">
          {progressCategoryConfig?.displayCategory || ""}
        </div>
        <div className="w-[60px] flex-shrink-0 text-right">
          {selectedQuestionIdx + 1 || 1}/{questions?.length || 1}
        </div>
      </div>

      <SlidesProgress
        max={progressCategoryConfig?.total}
        current={progressCategoryConfig?.current}
      />
    </div>
  );
};

export { ProgressSection };
